/**
 * =============================================================================
 * Página: ReferralAuditLog
 * Versão: 1.0.0 (Fase 8 — Bloco 8.7)
 *
 * Descrição:
 *   Painel administrativo de Auditoria do módulo de Indicação. Lista todas as
 *   alterações registradas pelos triggers de `affiliate_audit_logs`, com
 *   filtros por entidade, ação e busca por afiliado. Acesso restrito a admins
 *   (validação ocorre na RPC `get_affiliate_audit_trail`).
 * =============================================================================
 */
import { useMemo, useState } from 'react';
import { Shield, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  useAffiliateAuditTrail,
  type AffiliateAuditEntry,
} from '@/hooks/useAffiliateAuditTrail';

/** Mapeia entity_type → label legível em PT-BR. */
const ENTITY_LABEL: Record<string, string> = {
  affiliates: 'Afiliado',
  affiliate_commissions: 'Comissão',
  affiliate_payouts: 'Pagamento',
  affiliate_referrals: 'Indicação',
  affiliate_programs: 'Programa',
  affiliate_campaigns: 'Campanha',
  affiliate_links: 'Link',
};

/** Mapeia ação → label + variante visual. */
const ACTION_LABEL: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  create: { label: 'Criação', variant: 'default' },
  update: { label: 'Atualização', variant: 'secondary' },
  delete: { label: 'Exclusão', variant: 'destructive' },
};

const PAGE_SIZE = 50;

export default function ReferralAuditLog() {
  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState<string>('all');
  const [action, setAction] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<AffiliateAuditEntry | null>(null);

  const { data, isLoading } = useAffiliateAuditTrail({
    entityType: entityType === 'all' ? null : entityType,
    action: action === 'all' ? null : action,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  // Busca textual aplicada client-side sobre o nome do afiliado/usuário
  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    if (!search.trim()) return all;
    const term = search.trim().toLowerCase();
    return all.filter(
      (r) =>
        r.affiliate_name?.toLowerCase().includes(term) ||
        r.performed_by_name?.toLowerCase().includes(term),
    );
  }, [data, search]);

  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / PAGE_SIZE));

  return (
    <div className="space-y-4">
      {/* Header compacto padronizado */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-semibold">Auditoria</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Trilha de alterações nas entidades do módulo de Indicação.
        </p>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por afiliado ou autor da ação..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={entityType} onValueChange={(v) => { setEntityType(v); setPage(0); }}>
              <SelectTrigger><SelectValue placeholder="Entidade" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as entidades</SelectItem>
                {Object.entries(ENTITY_LABEL).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={action} onValueChange={(v) => { setAction(v); setPage(0); }}>
              <SelectTrigger><SelectValue placeholder="Ação" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as ações</SelectItem>
                <SelectItem value="create">Criação</SelectItem>
                <SelectItem value="update">Atualização</SelectItem>
                <SelectItem value="delete">Exclusão</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Entidade</TableHead>
                <TableHead>Ação</TableHead>
                <TableHead>Afiliado</TableHead>
                <TableHead>Executado por</TableHead>
                <TableHead className="w-[80px]">Detalhes</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                    Nenhum registro de auditoria encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const act = ACTION_LABEL[row.action] ?? { label: row.action, variant: 'outline' as const };
                  return (
                    <TableRow key={row.id}>
                      <TableCell className="text-sm">
                        {format(new Date(row.created_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })}
                      </TableCell>
                      <TableCell>{ENTITY_LABEL[row.entity_type] ?? row.entity_type}</TableCell>
                      <TableCell>
                        <Badge variant={act.variant}>{act.label}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {row.affiliate_name ?? <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {row.performed_by_name ?? (
                          <span className="text-muted-foreground italic">Sistema</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => setSelected(row)}>
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginação */}
      {(data?.total ?? 0) > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Página {page + 1} de {totalPages} · {data?.total} registro(s)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <ChevronLeft className="h-4 w-4" /> Anterior
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Próxima <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Drawer de detalhes — mostra diff old/new em JSON */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          {selected && (
            <>
              <SheetHeader>
                <SheetTitle>Detalhes da alteração</SheetTitle>
                <SheetDescription>
                  {ENTITY_LABEL[selected.entity_type] ?? selected.entity_type} ·{' '}
                  {ACTION_LABEL[selected.action]?.label ?? selected.action} ·{' '}
                  {format(new Date(selected.created_at), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })}
                </SheetDescription>
              </SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-muted-foreground">Executado por</p>
                    <p className="font-medium">
                      {selected.performed_by_name ?? 'Sistema'}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">IP</p>
                    <p className="font-medium">{selected.ip_address ?? '—'}</p>
                  </div>
                </div>

                {selected.old_data && (
                  <div>
                    <p className="text-sm font-medium mb-2">Dados antes</p>
                    <pre className="bg-muted p-3 rounded text-xs overflow-x-auto max-h-64">
                      {JSON.stringify(selected.old_data, null, 2)}
                    </pre>
                  </div>
                )}
                {selected.new_data && (
                  <div>
                    <p className="text-sm font-medium mb-2">Dados depois</p>
                    <pre className="bg-muted p-3 rounded text-xs overflow-x-auto max-h-64">
                      {JSON.stringify(selected.new_data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
